import { useCallback, useMemo, useRef, useState } from 'react';

export type DashboardFeed =
  | 'accountAiSettings'
  | 'accountBrand'
  | 'accountConfig'
  | 'adsAccounts'
  | 'domains'
  | 'dynamicPages'
  | 'forms'
  | 'globalSections'
  | 'quizzes'
  | 'sectionTypeConfigs'
  | 'subscription'
  | 'teamMembers';

const ALL_FEEDS: readonly DashboardFeed[] = [
  'accountAiSettings',
  'accountBrand',
  'accountConfig',
  'adsAccounts',
  'domains',
  'dynamicPages',
  'forms',
  'globalSections',
  'quizzes',
  'sectionTypeConfigs',
  'subscription',
  'teamMembers',
];

// Derived values map to the listener they are computed from, so reading
// `hasActiveSubscription` opens the subscription listener rather than
// resolving to undefined forever.
const FEED_BY_VALUE: Record<string, DashboardFeed> = {
  accountAiSettings: 'accountAiSettings',
  updateAccountAiSettings: 'accountAiSettings',
  accountBrand: 'accountBrand',
  updateAccountBrand: 'accountBrand',
  accountConfig: 'accountConfig',
  isAccountConfigLoaded: 'accountConfig',
  updateAccountConfig: 'accountConfig',
  adsAccounts: 'adsAccounts',
  adsAccountsAccountId: 'adsAccounts',
  allDomains: 'domains',
  domains: 'domains',
  primaryDomain: 'domains',
  dynamicPages: 'dynamicPages',
  forms: 'forms',
  globalSections: 'globalSections',
  quizzes: 'quizzes',
  sectionTypeConfigs: 'sectionTypeConfigs',
  getSectionTypeConfig: 'sectionTypeConfigs',
  getSectionListItemFieldConfig: 'sectionTypeConfigs',
  subscription: 'subscription',
  hasActiveSubscription: 'subscription',
  teamMembers: 'teamMembers',
};

// DashboardLayout, AiCreditUsagePopover and LimitExceededContext all read the
// subscription, so withholding it would only add a render pass.
const ALWAYS_ON: readonly DashboardFeed[] = ['subscription'];

export type RequestedFeeds = Record<DashboardFeed, boolean>;

export interface LazySubscriptions<T extends object> {
  requested: RequestedFeeds;
  track: (value: T) => T;
}

const toRecord = (feeds: ReadonlySet<DashboardFeed>): RequestedFeeds =>
  Object.fromEntries(
    ALL_FEEDS.map((feed) => [feed, feeds.has(feed)]),
  ) as RequestedFeeds;

export function useLazySubscriptions<T extends object>(): LazySubscriptions<T> {
  const [feeds, setFeeds] = useState<ReadonlySet<DashboardFeed>>(
    () => new Set(ALWAYS_ON),
  );
  const pending = useRef<Set<DashboardFeed>>(new Set());
  const flushQueued = useRef(false);

  // Each listener guards on its own boolean rather than on a callback, so
  // requesting a new feed cannot re-run the effects that are already
  // subscribed and make them tear down and reopen.
  const requested = useMemo(() => toRecord(feeds), [feeds]);

  // Opening a listener is a state update, so it cannot happen while a value is
  // being read during render. It also cannot wait on an effect in this
  // provider: most consumers call useContext directly, and a consumer can read
  // a value in a commit that never re-renders the provider. A microtask fires
  // after the commit that did the reading, whichever component made it.
  const flush = useCallback(() => {
    flushQueued.current = false;
    const wanted = [...pending.current];
    pending.current.clear();
    if (!wanted.length) return;

    setFeeds((current) => {
      const added = wanted.filter((feed) => !current.has(feed));
      if (!added.length) return current;

      return new Set([...current, ...added]);
    });
  }, []);

  // Reads are captured through a Proxy rather than by changing the ~100
  // consuming call sites.
  const track = useCallback(
    (value: T): T =>
      new Proxy(value, {
        get(target, prop, receiver) {
          const feed =
            typeof prop === 'string' ? FEED_BY_VALUE[prop] : undefined;
          if (feed && !pending.current.has(feed)) {
            pending.current.add(feed);
            if (!flushQueued.current) {
              flushQueued.current = true;
              queueMicrotask(flush);
            }
          }
          return Reflect.get(target, prop, receiver);
        },
      }),
    [flush],
  );

  return useMemo(() => ({ requested, track }), [requested, track]);
}
